import React, {useContext, useEffect, useState} from 'react';
import Calendar from './availability-slot-picker/calendar';
import TimeSlotGrid from './availability-slot-picker/time-slot-grid';
import {WebsiteContext} from '../../../websites';
import {formatDateToFriendlyDate} from "../../../websites/src/blocks/account-block";
import axios from "axios";
import classNames from "classnames";

export const generateTimeSlots = (workHours, sessionLength, forDate = null) => {
    if (!workHours) return [];

    const [start, end] = workHours.split('-');
    const startTime = parseTime(start);
    const endTime = parseTime(end);
    const now = new Date();
    const slots = [];

    let current = new Date(startTime);

    // If the slot is for today, skip past time
    if (forDate && now.toDateString() === forDate.toDateString()) {
        while (current <= now) {
            current.setMinutes(current.getMinutes() + sessionLength);
        }
    }

    while (current < endTime) {
        slots.push(formatTime(current));
        current.setMinutes(current.getMinutes() + sessionLength);
    }

    return slots;
};

const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

const formatTime = (date) => {
    return date.toTimeString().slice(0, 5);
};

const formatFriendlyDate = (date) => {
    return date.toLocaleDateString('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const extractTimetable = (department) => {
    if (!department) return [];

    // Case 1: Already in new timetable format
    if (department.departmentWorkHours?.timetable) {
        return department.departmentWorkHours.timetable;
    }

    // Case 2: Legacy flat object
    const legacy = {
        weekdayWorkHours: department.weekdayWorkHours,
        saturdayWorkHours: department.saturdayWorkHours,
        sundayWorkHours: department.sundayWorkHours
    };

    const days = [
        {key: 'Sunday', source: legacy.sundayWorkHours},
        {key: 'Monday', source: legacy.weekdayWorkHours},
        {key: 'Tuesday', source: legacy.weekdayWorkHours},
        {key: 'Wednesday', source: legacy.weekdayWorkHours},
        {key: 'Thursday', source: legacy.weekdayWorkHours},
        {key: 'Friday', source: legacy.weekdayWorkHours},
        {key: 'Saturday', source: legacy.saturdayWorkHours}
    ];

    return days.map(({key, source}) => {
        if (!source || source.trim() === '') {
            return {day: key, status: 'closed'};
        }

        const [open, closed] = source.split('-');
        return {
            day: key,
            open: open?.trim(),
            closed: closed?.trim(),
            status: 'open'
        };
    });
};

export default function AvailabilitySlotPicker({timeSlots, sessionLength = 30, locationId, ...props}) {
    const websiteContext = useContext(WebsiteContext);
    const [selectedDate, setSelectedDate] = useState(null);
    const [bookedSlots, setBookedSlots] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dayToHoursMap, setDayToHoursMap] = useState({});
    const [timetableData, setTimetableData] = useState(timeSlots || []);
    const showEdit = props.editing && !props.system;
    const [selectedTime, setSelectedTime] = useState(null);

    useEffect(() => {
        if (!selectedDate && Object.keys(dayToHoursMap).length) {
            const today = new Date();
            const searchLimit = 60; // Number of days ahead to search
            for (let i = 0; i < searchLimit; i++) {
                const date = new Date(today);
                date.setDate(today.getDate() + i);
                const weekday = date.toLocaleDateString('en-AU', {weekday: 'long'});
                const workHours = dayToHoursMap[weekday];
                if (workHours) {
                    const slots = generateTimeSlots(workHours, sessionLength, date);
                    if (slots.length > 0) {
                        setSelectedDate(date);
                        break;
                    }
                }
            }
        }
    }, [dayToHoursMap, selectedDate, sessionLength]);

    useEffect(() => {
        if (selectedTime) {
            setSelectedTime(null);
            props.onChange(null, props.name, null);
        }
    }, [selectedDate]);

    // load from context if locationId is given
    useEffect(() => {
        if (locationId && websiteContext?.website?.departments?.length) {
            const dept = websiteContext.website.departments.find((d) => d.id === locationId);
            if (dept?.departmentWorkHours?.timetable) {
                setTimetableData(extractTimetable(dept));
            }
        } else if (locationId) {
            axios.get(`/api/v2/departments/${locationId}`)
                .then(({data}) => {
                    setTimetableData(extractTimetable(data));
                })
        }
    }, [locationId, websiteContext]);

    // build day-to-hours map
    useEffect(() => {
        if (!timetableData?.length) return;

        const flat = {};
        timetableData.forEach((entry) => {
            flat[entry.day] = entry.status === 'open' ? `${entry.open}-${entry.closed}` : null;
        });
        setDayToHoursMap(flat);
    }, [timetableData]);

    useEffect(() => {
        const loadSlots = async () => {
            if (!selectedDate || !Object.keys(dayToHoursMap).length) return;
            setLoading(true);
            try {
                const dateStr = selectedDate.toISOString().split('T')[0];
                const booked = []; // replace with actual API call
                setBookedSlots(booked);

                const dayOfWeek = selectedDate.toLocaleString('en-US', {weekday: 'long'});
                const workHours = dayToHoursMap[dayOfWeek];

                const slots = generateTimeSlots(workHours, sessionLength, selectedDate);
                setAvailableSlots(slots);
            } finally {
                setLoading(false);
            }
        };

        loadSlots();
    }, [dayToHoursMap, selectedDate, sessionLength]);

    const handleTimeClick = (e, time) => {
        e.preventDefault();
        if (!selectedDate) return;

        // Combine selectedDate and time into one Date object
        const [hours, minutes] = time.split(':');
        const date = new Date(selectedDate);
        date.setHours(Number(hours), Number(minutes), 0, 0);

        const formatted = formatDateToFriendlyDate(date);
        props.onChange(e, props.name, formatted);

        setSelectedTime(time);
    };

    let radius = '0';
    useEffect(() => {
        if (!props.pageRef?.current) return;
        radius = getComputedStyle(props.pageRef.current)?.getPropertyValue('--container-roundness');
    }, [props.pageRef]);

    return (
        <div className={"relative w-full"}>
            <input type="text" className="pointer-none opacity-0 absolute bottom-0 left-0" name={props.name}
                   defaultValue={props.value} required={!props.editing && !!props.required}/>
            {showEdit && (
                <div className="block w-full flex justify-between mb-2">
                    <label htmlFor={props.id} className="block text-sm font-semibold text-gray-700">
                        {props.label}
                    </label>
                    {props.editing && !props.system && (
                        <span
                            className="text-xs font-medium text-primary-500 leading-5 cursor-pointer hover:underline"
                            onClick={props.onEditFieldSettings}
                        >
              Edit
            </span>
                    )}
                </div>
            )}

            <div
                style={{borderRadius: radius === '0px' ? '0' : ''}}
                className={classNames(
                    showEdit ? 'pointer-events-none' : '',
                    "transition-all duration-700 ease-in-out bg-white p-4 rounded-theme border border-gray-300 shadow-none"
                )}
            >
                {!timetableData.length || !selectedDate ? (
                    <div className="text-center py-16 text-gray-400">Loading timetable...</div>
                ) : (
                    <div className="relative flex flex-col sm:flex-row gap-6">
                        {/* Calendar Panel */}
                        <div className="sm:w-3/5 w-full z-10 shrink-0">
                            <Calendar
                                {...props}
                                selectedDate={selectedDate}
                                onDateSelect={setSelectedDate}
                                timeSlots={timetableData} // must be an array
                            />
                        </div>

                        {/* Time Slots Panel */}
                        <div className="sm:w-2/5 w-full">
                            {!selectedDate ? (
                                <div className="text-sm font-normal text-gray-500">
                                    Please select a date to view available times.
                                </div>
                            ) : (
                                <>
                                    <div className="text-sm font-semibold mb-3 my-2">
                                        {formatFriendlyDate(selectedDate)}
                                    </div>
                                    {loading ? (
                                        <div className="text-sm text-gray-400 animate-pulse">Loading available
                                            times...</div>
                                    ) : (
                                        <TimeSlotGrid
                                            {...props}
                                            slots={availableSlots}
                                            bookedSlots={bookedSlots}
                                            onSelect={handleTimeClick}
                                            selectedTime={selectedTime}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}